import React from "react";
import { useApp } from "@/context/AppContext";
import { AIRPORTS } from "@/i18n";
import { Plane, Clock, Briefcase, Check } from "lucide-react";
import { motion } from "framer-motion";
import { paxLabel } from "@/components/PassengerPicker";

export const airportName = (code, lang) => (AIRPORTS.find((a) => a.code === code)?.[lang] || code);

export const FlightCard = ({ f, index = 0, selected, onSelect, onBook }) => {
  const { t, lang, money } = useApp();
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06 }}
      data-testid={`flight-card-${f.id}`}
      onClick={onSelect}
      className={`bg-white rounded-2xl p-5 border shadow-sm flex flex-col sm:flex-row items-center gap-5 transition-colors ${onSelect ? "cursor-pointer" : ""} ${selected ? "border-tigris ring-2 ring-tigris/30" : "border-slate-100"}`}>
      <div className="flex items-center gap-3 w-full sm:w-auto">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selected ? "bg-tigris text-white" : "bg-tigris/10 text-tigris"}`}>
          {selected ? <Check size={22} /> : <Plane size={22} />}
        </div>
        <div>
          <div className="font-bold text-slate-800 text-sm">{f[`airline_${lang}`]}</div>
          <div className="text-xs text-slate-400">{f.airline_code}</div>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-1">
        <div className="text-center">
          <div className="text-xl font-extrabold text-slate-800" dir="ltr">{f.depart}</div>
          <div className="text-xs text-slate-400">{airportName(f.origin, lang)}</div>
        </div>
        <div className="flex-1 flex flex-col items-center min-w-[90px]">
          <div className="text-[11px] text-slate-400 flex items-center gap-1"><Clock size={11} /> {Math.floor(f.duration_min / 60)}h {f.duration_min % 60}m</div>
          <div className="w-full h-px bg-slate-200 my-1.5 relative"><span className="absolute -top-1 start-1/2 w-2 h-2 rounded-full bg-gold" /></div>
          <div className="text-[11px] text-tigris font-semibold">{f.stops === 0 ? t.common.direct : `${f.stops} ${t.common.stops}`}</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-extrabold text-slate-800" dir="ltr">{f.arrive}</div>
          <div className="text-xs text-slate-400">{airportName(f.destination, lang)}</div>
        </div>
      </div>
      <div className="flex flex-col items-center gap-1 sm:border-s sm:ps-5 border-slate-100 w-full sm:w-auto">
        <div className="text-[11px] text-slate-400 flex items-center gap-1"><Briefcase size={11} /> {f.baggage_kg}kg</div>
        <div className="text-2xl font-extrabold text-tigris" data-testid={`flight-total-${f.id}`}>{money(f.total)}</div>
        {f.passengers > 1 && <div className="text-[11px] text-slate-400" data-testid={`flight-pax-${f.id}`}>{money(f.price)} · {paxLabel(f, t.pax)}</div>}
        {onBook && (
          <button data-testid={`book-flight-${f.id}`} onClick={(e) => { e.stopPropagation(); onBook(); }}
            className="btn-gold px-6 py-2 rounded-xl font-bold text-sm w-full">{t.common.bookNow}</button>
        )}
        {onSelect && (
          <span data-testid={`select-flight-${f.id}`} className={`text-xs font-bold px-4 py-1.5 rounded-lg ${selected ? "bg-tigris text-white" : "bg-slate-100 text-slate-600"}`}>
            {selected ? t.flights.selected : t.flights.select}
          </span>
        )}
      </div>
    </motion.div>
  );
};
